import React, { useMemo } from "react";
import { FieldArray, useFormikContext } from "formik";
import * as Yup from "yup";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { maskPatternToValidationRegex } from "commons/utils/inputMask.utils";
import { LINK_FIELD_VALIDATION } from "lookup/UnprocessedFaxListConstantsV2";

const BOOTSTRAP_GRID_COLUMNS = 12;
const MAX_FIELDS_PER_ROW = 3;

/** Split row width evenly across fields in the same row (12-col grid). */
export const getLinkFieldColSizes = (fieldCount) => {
  if (fieldCount <= 0) return [];

  const baseCol = Math.floor(BOOTSTRAP_GRID_COLUMNS / fieldCount);
  const remainder = BOOTSTRAP_GRID_COLUMNS % fieldCount;

  return Array.from({ length: fieldCount }, (_, index) =>
    baseCol + (index < remainder ? 1 : 0)
  );
};

const chunkIntoRows = (items, maxPerRow = MAX_FIELDS_PER_ROW) => {
  const rows = [];
  for (let i = 0; i < items.length; i += maxPerRow) {
    rows.push(items.slice(i, i + maxPerRow));
  }
  return rows;
};

const buildFieldValidation = (field, noOfPages) => {
  const { label } = field;
  const kind = field.validation?.kind;

  switch (kind) {
    case LINK_FIELD_VALIDATION.STRING:
      return Yup.string()
        .typeError(`Enter a valid ${label}`)
        .required(`${label} is required`);
    case LINK_FIELD_VALIDATION.NUMERIC_STRING:
      return Yup.string()
        .matches(/^\d+$/, `${label} must be a number`)
        .required(`${label} is required`);
    case LINK_FIELD_VALIDATION.MASKED_STRING: {
      const maskPattern = field.validation?.maskPattern || "";
      const maskRegex = maskPatternToValidationRegex(maskPattern);
      return Yup.string()
        .matches(
          maskRegex,
          `${label} must match the format ${maskPattern || "expected pattern"}`
        )
        .required(`${label} is required`);
    }
    case LINK_FIELD_VALIDATION.PAGE_NUMBER:
      return Yup.number()
        .required(`${label} is required`)
        .min(1, `${label} must be greater than or equal to 1`)
        .max(
          noOfPages || 999,
          `${label} must be less than or equal to ${noOfPages || 999}`
        );
    case LINK_FIELD_VALIDATION.REQUIRED_STRING:
      return Yup.string().required(`${label} is required`);
    case LINK_FIELD_VALIDATION.OPTIONAL_STRING:
      return Yup.string()
        .typeError(`Enter valid ${label}`)
        .nullable()
        .notRequired();
    default:
      return null;
  }
};

export const buildUnprocessedFaxLinkValidationSchema = (linkFieldsConfig = [], noOfPages) => {
  const linkPageShape = {
    isUpsideDown: Yup.boolean(),
    overrideAssociatedSwitch: Yup.boolean(),
  };

  linkFieldsConfig.forEach((field) => {
    const fieldValidation = buildFieldValidation(field, noOfPages);
    if (fieldValidation) {
      linkPageShape[field.name] = fieldValidation;
    }
  });

  return Yup.object().shape({
    linkPages: Yup.array().of(Yup.object().shape(linkPageShape)),
  });
};

export const isLinkPageFieldMissing = (page, linkFieldsConfig = []) =>
  linkFieldsConfig.some(
    (field) =>
      field.validation?.kind !== LINK_FIELD_VALIDATION.OPTIONAL_STRING &&
      !page[field.name]
  );

/**
 * Map form link page(s) to API payload objects using `apiField` from linkFieldsConfig.
 * Form field `name` is the Formik key; `apiField` is the property name sent to the API.
 */
export const mapLinkPageToApiPayload = (
  page,
  linkFieldsConfig = [],
  {
    supplementalLinkPagePayload = { isUpsideDown: "isUpsideDown" },
    faxProgram,
    linkPagePayloadKeys = { faxProgram: "faxProgram" },
  } = {}
) => {
  const item = {};

  linkFieldsConfig.forEach((field) => {
    if (field.includeInApiPayload === false) return;

    const apiKey = field.apiField ?? field.name;
    const value = page[field.name];

    if (value !== undefined && value !== null && value !== "") {
      item[apiKey] = value;
    }
  });

  Object.entries(supplementalLinkPagePayload).forEach(([apiKey, formField]) => {
    const value = page[formField];
    item[apiKey] = apiKey === "isUpsideDown" ? Boolean(value) : value;
  });

  if (faxProgram) {
    const faxProgramKey = linkPagePayloadKeys.faxProgram || "faxProgram";
    item[faxProgramKey] = faxProgram;
  }

  return item;
};

export const mapLinkPagesToApiPayload = (
  linkPages = [],
  linkFieldsConfig = [],
  options = {}
) => linkPages.map((page) => mapLinkPageToApiPayload(page, linkFieldsConfig, options));

const getLinkPageFieldError = (errors, touched, index, fieldName) => {
  if (
    errors?.linkPages?.[index]?.[fieldName] &&
    touched?.linkPages?.[index]?.[fieldName]
  ) {
    return errors.linkPages[index][fieldName];
  }
  return null;
};

const LinkPageFieldControl = ({
  field,
  linkPage,
  index,
  noOfPages,
  handleChange,
  handleBlur,
  disabled = false,
}) => {
  const fieldName = `linkPages.${index}.${field.name}`;
  const value = linkPage[field.name] || "";

  if (field.inputType === "number") {
    return (
      <Form.Control
        type="number"
        step={1}
        min={1}
        max={noOfPages}
        onChange={handleChange}
        onBlur={handleBlur}
        value={value}
        name={fieldName}
        disabled={disabled}
      />
    );
  }

  return (
    <Form.Control
      type="text"
      onChange={handleChange}
      onBlur={handleBlur}
      value={value}
      name={fieldName}
      disabled={disabled}
      placeholder={field.placeholder}
    />
  );
};

const LinkPageConfigField = ({
  field,
  linkPage,
  pageIndex,
  colMd,
  noOfPages,
  errors,
  touched,
  handleChange,
  handleBlur,
}) => (
  <Col xs={12} md={colMd}>
    <Form.Group>
      <Form.Label>{field.label}</Form.Label>
      <LinkPageFieldControl
        field={field}
        linkPage={linkPage}
        index={pageIndex}
        noOfPages={noOfPages}
        handleChange={handleChange}
        handleBlur={handleBlur}
        disabled={field.disabled === true}
      />
      {field.helperText && (
        <Form.Text className="text-muted" style={{ fontSize: "0.875rem" }}>
          {field.helperText}
        </Form.Text>
      )}
      <Form.Text className="text-small text-danger">
        {getLinkPageFieldError(errors, touched, pageIndex, field.name)}
      </Form.Text>
    </Form.Group>
  </Col>
);

const OrientationField = ({ linkPage, pageIndex, colMd, handleChange }) => (
  <Col xs={12} md={colMd}>
    <Form.Group style={{ marginBottom: 0 }}>
      <Form.Label>Orientation</Form.Label>
      <Form.Check
        type="checkbox"
        value={linkPage.isUpsideDown}
        name={`linkPages.${pageIndex}.isUpsideDown`}
        label="Is Upside Down"
        onChange={handleChange}
        checked={linkPage.isUpsideDown}
      />
    </Form.Group>
  </Col>
);

const UnprocessedFaxLinkPageFields = ({ linkFieldsConfig = [], noOfPages }) => {
  const { values, errors, touched, handleChange, handleBlur } = useFormikContext();

  const fieldRows = useMemo(() => {
    const items = [
      ...linkFieldsConfig.map((field) => ({ type: "link", field })),
      { type: "orientation" },
    ];
    return chunkIntoRows(items);
  }, [linkFieldsConfig]);

  const rowColSizes = useMemo(
    () => fieldRows.map((row) => getLinkFieldColSizes(row.length)),
    [fieldRows]
  );

  return (
    <FieldArray
      name="linkPages"
      render={() => (
        <>
          {values.linkPages.map((linkPage, pageIndex) => (
            <div key={pageIndex}>
              {fieldRows.map((row, rowIndex) => (
                <Row key={`${pageIndex}-row-${rowIndex}`} className={rowIndex > 0 ? "mt-2" : undefined}>
                  {row.map((item, colIndex) => {
                    const colMd = rowColSizes[rowIndex][colIndex];

                    if (item.type === "orientation") {
                      return (
                        <OrientationField
                          key="orientation"
                          linkPage={linkPage}
                          pageIndex={pageIndex}
                          colMd={colMd}
                          handleChange={handleChange}
                        />
                      );
                    }

                    return (
                      <LinkPageConfigField
                        key={item.field.name}
                        field={item.field}
                        linkPage={linkPage}
                        pageIndex={pageIndex}
                        colMd={colMd}
                        noOfPages={noOfPages}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                      />
                    );
                  })}
                </Row>
              ))}
            </div>
          ))}
          {values.linkPages.length > 0 && <hr />}
        </>
      )}
    />
  );
};

export default UnprocessedFaxLinkPageFields;
